import { Theme } from 'helpers/theme';
import React, { useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { graphql } from 'relay-runtime';
import SimulatorImage, { FilterOptions } from './SimulatorImage';
import IdentPoint from './IdentPoint';
import IdentPopup from './IdentPopup';
import { Coords, SuspectItem } from 'types/evrima';
import { animated, useSpring, config } from '@react-spring/web';
import { useRouter } from 'found';
import { useMediaQuery } from 'react-responsive';
import useDimensions from 'features/simulator/hooks/dimensions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandBackPointUp,
  faReflectHorizontal
} from '@fortawesome/pro-solid-svg-icons';
import { v4 } from 'uuid';
import { SimulatorDisplay_imageResult } from './__generated__/SimulatorDisplay_imageResult.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  display: {
    marginBottom: '50px',
    borderRadius: '20px',
    border: '1px solid #2E2C2C',
    padding: 20,
    display: 'grid',
    height: '100%',
    maxHeight: 'calc(100vh - 380px)',
    gap: '20px'
  },
  mainImage: {
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative'
  },
  carousel: {
    display: 'flex',
    position: 'absolute'
  },
  secondaryImage: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 12,
    height: '100%'
  },
  rightItems: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  swapButtonContainer: {
    position: 'absolute',
    bottom: 16,
    right: 18
  },
  swapButton: {
    gap: '10px',
    width: 151,
    color: 'white',
    border: '1px solid #2e2e2e69',
    backdropFilter: 'blur(5px)',
    cursor: 'pointer',
    display: 'flex',
    padding: '10px 25px',
    fontSize: 13,
    background: '#67676780',
    textAlign: 'center',
    alignItems: 'center',
    borderRadius: 10,
    justifyContent: 'center',
    '&:hover': {
      background: '#4d4b4bba'
    }
  },
  swapIcon: {},
  identifyHelper: {
    position: 'absolute',
    top: 10,
    left: 10
  },
  identifyButton: {
    borderRadius: 8,
    background: '#0000006e',
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 300,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    fontFamily: 'filson-pro'
  }
}));

export type RollingState =
  | {
      isRolling: boolean;
      hasRolled: boolean;
      hideState: boolean;
    }
  | undefined;

type Props = {
  imageResult?: SimulatorDisplay_imageResult;
  onSuspectChange: (item: SuspectItem) => void;
  suspectItem: SuspectItem;
  rolling: RollingState;
  setRolling: (rolling: RollingState) => void;
  onLoad?: () => void;
  zoomStatus?: number;
  sidebarHidden: boolean;
  filter?: FilterOptions[];
  showThreat?: boolean;
};

type Images = {
  nextImage: Image;
  currentImage: Image;
};

type Image = {
  image: SimulatorDisplay_imageResult['image'] | undefined;
  height: number;
  secondaryHeight: number;
  uuid: string;
};

function SimulatorDisplay({
  imageResult,
  suspectItem,
  onSuspectChange,
  rolling,
  sidebarHidden,
  filter,
  showThreat,
  setRolling,
  zoomStatus,
  onLoad
}: Props) {
  const styles = useStyles();
  const { router, match } = useRouter();
  const [tempSuspectItem, setTempSuspectItem] = React.useState<SuspectItem>(
    undefined
  );

  const selectedImage = imageResult?.['image'];

  const [images, setImages] = React.useState<Images>({
    nextImage: {
      image: selectedImage,
      height: 0,
      secondaryHeight: 0,
      uuid: v4()
    },
    currentImage: {
      image: selectedImage,
      height: 0,
      secondaryHeight: 0,
      uuid: v4()
    }
  });

  const [swapped, setSwapped] = React.useState<boolean>(false);

  const nextImage = images.nextImage;
  let currentImage = images.currentImage;

  if (selectedImage) {
    currentImage.image = selectedImage;
  }

  const isSmallScreen = useMediaQuery({
    query: '(max-width: 1200px)'
  });
  const isLargeScreen = useMediaQuery({
    query: '(min-width: 1400px)'
  });

  let layout: 'small' | 'normal' | 'large' = 'normal';

  if (isSmallScreen) {
    layout = 'small';
  } else {
    layout = 'normal';
    if (sidebarHidden) {
      layout = 'large';
    }

    if (isLargeScreen) {
      layout = 'large';
    }
  }

  const [mainRef, mainDims] = useDimensions([
    sidebarHidden,
    currentImage,
    nextImage
  ]);
  const [sideRef, sideDims] = useDimensions([
    sidebarHidden,
    currentImage,
    nextImage
  ]);
  const [displayRef, displayDims] = useDimensions([
    sidebarHidden,
    currentImage,
    nextImage
  ]);

  const imageHeight = displayDims?.height ? displayDims.height - 40 : 450;

  const MAIN_DIMS = {
    width: mainDims?.width ?? 0,
    height: mainDims?.height ?? 0
  };
  const SIDE_DIMS = {
    width: sideDims?.width ?? 0,
    height: sideDims?.height ?? 0
  };

  let { left, smallLeft } = useSpring({
    left: rolling?.isRolling ? -MAIN_DIMS.width : 0,
    smallLeft: rolling?.isRolling ? -SIDE_DIMS.width : 0,
    config: { ...config.default, duration: 3000 },
    onRest: () => {
      if (rolling?.isRolling) {
        // Set the image just seen to undefined so that it can't be shown again
        setImages((img) => ({
          ...img,
          currentImage: img.nextImage,
          nextImage: {
            image: undefined,
            height: 0,
            uuid: v4(),
            secondaryHeight: 0
          }
        }));
        setRolling({
          isRolling: false,
          hasRolled: !rolling.hasRolled,
          hideState: true
        });
      }
    },
    immediate: !rolling?.isRolling
  });

  // maps the images to the correct positions on the screen

  const buildImage = (
    variant: Image,
    showTop: boolean
  ): {
    image: Image;
    url: string | undefined;
    threatURL: string | undefined;
    position: any;
    key: string;
    isTop: boolean;
  } => ({
    key: variant.uuid,
    image: variant,
    url: variant?.image?.normal?.images[showTop ? 'topURL' : 'sideURL'],
    threatURL:
      variant?.image?.threat?.threatXray?.images[
        showTop ? 'topURL' : 'sideURL'
      ],
    position:
      variant?.image?.threat?.[showTop ? 'topPosition' : 'sidePosition'],
    isTop: showTop
  });

  const mainImage = buildImage(currentImage, !swapped); // The image that is currently being shown
  const mainNextImage = buildImage(nextImage, !swapped); // The pre-prepared image that is hidden

  const sideImage = buildImage(currentImage, swapped); // The image that is currently being shown (but smaller)
  const sideNextImage = buildImage(nextImage, swapped);

  const mainImages = [
    <SimulatorImage
      key={mainImage.key}
      width={MAIN_DIMS.width}
      height={imageHeight}
      backgroundURL={mainImage.url}
      threatURL={mainImage.threatURL}
      position={mainImage.position}
      showThreat={showThreat}
      zoomStatus={zoomStatus}
      filter={filter}
      onLoad={(dims) => {
        onLoad && onLoad();
      }}
      onClick={(isThreat, mousePos, imagePos) => {
        if (selectedImage) return;
        onSuspectChange(undefined);
        setTempSuspectItem({
          mousePos,
          imagePos,
          isSearch: false,
          isCorrect: !!mainImage?.image?.image?.threat && isThreat,
          isTopView: mainImage.isTop
        });
      }}
    />,
    <SimulatorImage
      key={mainNextImage.key}
      hidden={!rolling?.isRolling}
      width={MAIN_DIMS.width}
      height={imageHeight}
      showThreat={showThreat}
      backgroundURL={mainNextImage.url}
      threatURL={mainNextImage.threatURL}
      position={mainNextImage.position}
      onClick={(isThreat, mousePos, imagePos) => {
        if (selectedImage) return;
        onSuspectChange(undefined);
        setTempSuspectItem({
          mousePos,
          imagePos,
          isSearch: false,
          isCorrect: !!mainNextImage.image?.image?.threat && isThreat,
          isTopView: mainNextImage.isTop
        });
      }}
    />
  ];

  const smallImageHeight = layout == 'normal' ? 150 : imageHeight;

  const smallImages = [
    <SimulatorImage
      key={sideImage.key}
      width={SIDE_DIMS.width}
      height={smallImageHeight}
      filter={filter}
      showThreat={showThreat}
      zoomStatus={zoomStatus}
      onLoad={(dims) => {
        onLoad && onLoad();
      }}
      backgroundURL={sideImage.url}
      threatURL={sideImage.threatURL}
      position={sideImage.position}
      onClick={(isThreat, mousePos, imagePos) => {
        if (selectedImage) return;
        onSuspectChange(undefined);
        setTempSuspectItem({
          mousePos,
          imagePos,
          isSearch: false,
          isCorrect: !!sideImage.image?.image?.threat && isThreat,
          isTopView: sideImage.isTop
        });
      }}
    />,
    <SimulatorImage
      key={sideNextImage.key}
      width={SIDE_DIMS.width}
      height={smallImageHeight}
      hidden={!rolling?.isRolling}
      showThreat={showThreat}
      onLoad={(dims) => {
        onLoad && onLoad();
      }}
      backgroundURL={sideNextImage.url}
      threatURL={sideNextImage.threatURL}
      position={sideNextImage.position}
      onClick={(isThreat, mousePos, imagePos) => {
        if (selectedImage) return;
        onSuspectChange(undefined);
        setTempSuspectItem({
          mousePos,
          imagePos,
          isSearch: false,
          isCorrect: !!sideNextImage.image?.image?.threat && isThreat,
          isTopView: sideNextImage.isTop
        });
      }}
    />
  ];

  return (
    <div
      className={styles.display}
      style={{
        gridTemplateColumns:
          layout == 'small' ? '1fr' : layout == 'large' ? '1fr 1fr' : '2fr 1fr'
      }}
      ref={displayRef}
    >
      <div
        className={styles.mainImage}
        ref={mainRef}
        style={{ height: imageHeight }}
      >
        <animated.div
          className={styles.carousel}
          style={{
            left: selectedImage ? 0 : left,
            width: MAIN_DIMS.width * 2
          }}
        >
          {mainImages}
        </animated.div>
        <div className={styles.swapButtonContainer}>
          <div
            className={styles.swapButton}
            onClick={() => setSwapped(!swapped)}
          >
            <FontAwesomeIcon
              icon={faReflectHorizontal}
              className={styles.swapIcon}
            />{' '}
            Swap views
          </div>
        </div>
      </div>
      {layout !== 'small' && (
        <div className={styles.rightItems}>
          <div
            className={styles.secondaryImage}
            ref={sideRef}
            style={{ height: smallImageHeight }}
          >
            <animated.div
              className={styles.carousel}
              // @ts-ignore
              style={{
                left: selectedImage ? 0 : smallLeft,
                width: SIDE_DIMS.width * 2
              }}
            >
              {smallImages}
            </animated.div>
          </div>
        </div>
      )}

      {(suspectItem || tempSuspectItem) && (
        <IdentPoint
          x={
            ((suspectItem || tempSuspectItem)?.mousePos.x ?? 0) -
            (sidebarHidden ? 80 : 400)
          }
          y={(suspectItem || tempSuspectItem)?.mousePos.y ?? 0}
          onSelect={(isSearch) => {
            if (tempSuspectItem) {
              onSuspectChange({
                ...tempSuspectItem,
                isSearch
              });
            }
            if (suspectItem) {
              onSuspectChange({
                ...suspectItem,
                isSearch
              });
            }

            setTempSuspectItem(undefined);
          }}
          onExit={() => {
            setTempSuspectItem(undefined);
            onSuspectChange(undefined);
          }}
          variant={
            tempSuspectItem
              ? 'temp'
              : suspectItem?.isSearch
              ? 'search'
              : 'threat'
          }
        />
      )}
    </div>
  );
}

const SimulatorDisplayFrag = createFragmentContainer(SimulatorDisplay, {
  imageResult: graphql`
    fragment SimulatorDisplay_imageResult on ImageResult {
      uuid
      identifiedStatus
      image {
        normal {
          images {
            topURL
            sideURL
          }
        }
        threat {
          threatXray {
            images {
              topURL
              sideURL
            }
          }
          topPosition {
            x
            y
            scale
            rotation
          }
          sidePosition {
            x
            y
            scale
            rotation
          }
        }
      }
    }
  `
});

export default SimulatorDisplayFrag;
